import { faPlus } from "@fortawesome/pro-light-svg-icons"
import { Button, Modal, ModalHeader, Typography } from "@joonapp/web-shared"
import { useTranslation } from "react-i18next"
import { create } from "zustand"

import useAddEditRewardModalStore from "./hooks/useAddEditRewardModalStore"
import { useRewardTemplatesQuery } from "../../networking/rewards/queries"
import { CustomRewardTemplate } from "../../types/rewards"

interface RewardTemplatesModalStore {
  isOpen: boolean
  onOpen: () => void
  onClose: () => void
}

export const useRewardTemplatesModal = create<RewardTemplatesModalStore>(
  (set) => ({
    isOpen: false,
    onOpen: () => set({ isOpen: true }),
    onClose: () => set({ isOpen: false }),
  })
)

const RewardTemplates = () => {
  const { isOpen, onClose: closeRewardTemplatesModal } =
    useRewardTemplatesModal()
  const { data: templates } = useRewardTemplatesQuery()
  const { onOpen: openAddEditRewardModal } = useAddEditRewardModalStore()
  const { t } = useTranslation()

  const handleCreateCustomReward = () => {
    openAddEditRewardModal()
    closeRewardTemplatesModal()
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={closeRewardTemplatesModal}
      animate
      mobileFull
      style={{ width: "min(500px, 100vw)" }}
    >
      <ModalHeader
        title={t("RT_TITLE")}
        onClose={closeRewardTemplatesModal}
        showBorderBottom
      />
      <div className="flex flex-col gap-4 p-6 pt-4 overflow-y-auto">
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {templates?.map((template: CustomRewardTemplate, i: number) => (
            <RewardTemplate template={template} key={i} />
          ))}
        </div>
      </div>
      <div className="flex flex-col items-center gap-4 p-6 pt-3 bg-gray-100">
        <Typography variant="bodyBold">{t("RT_CANT_FIND")}</Typography>
        <Button
          text={t("RT_CREATE_CUSTOM")}
          onClick={handleCreateCustomReward}
          icon={faPlus}
          fullWidth
        />
      </div>
    </Modal>
  )
}

const RewardTemplate = ({ template }: { template: CustomRewardTemplate }) => {
  const { onOpen } = useAddEditRewardModalStore()
  const { onClose } = useRewardTemplatesModal()
  const { t } = useTranslation()

  const onClickUseTemplate = () => {
    setTimeout(() => onOpen(template), 100)
    onClose()
  }

  return (
    <div className="flex flex-row justify-between items-center gap-4 border border-borderPrimary rounded-md p-3">
      <div className="flex flex-row gap-4 items-center w-full">
        <div style={{ display: "flex", gap: "8px" }}>
          <Typography variant="bodyBold">{template.emoji}</Typography>
          <Typography variant="bodyBold">{template.title}</Typography>
        </div>
        <div className="flex flex-row gap-1 items-center ml-auto">
          <img
            src="/images/icons/coin-icon.png"
            alt="coin"
            className="w-4 h-4"
          />
          <Typography variant="bodySmall">{template.cost} </Typography>
        </div>
      </div>
      <Button
        text={t("RT_USE")}
        onClick={onClickUseTemplate}
        buttonType="secondary"
        size="small"
      />
    </div>
  )
}

export default RewardTemplates
